import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { UserPet, PetMovement } from '@typings/pets/pet.interface';
import {
  PetPersonalityTypeScripts,
} from '../PetPersonality.ts/PetPersonalityType';
import { useDocument } from '@nandorojo/swr-firestore';

type PersonalitiesDoc = {
  types: Record<string, string>;
  scripts: Record<string, string[]>;
  updatedAt?: any;
};

interface Props {
  pet: UserPet;
  movement?: PetMovement;
  onClick?: () => void;
  containerBounds?: DOMRect;
}

const defaultMovement: PetMovement = {
  speed: 0.05,
  smoothness: 0.8,
  mouseSensitivity: 0.1,
  boundaries: { minX: 50, maxX: 400, minY: 50, maxY: 300 },
};

type InteractionType = 'none' | 'click' | 'drag' | 'walking';

const GRAVITY = 1800;
const WALK_SPEED = 50;
const BOUNCE_DAMPING = 0.3;

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const PetCharacter: React.FC<Props> = ({
  pet,
  movement = defaultMovement,
  onClick,
  containerBounds,
}) => {
  const petRef = useRef<HTMLDivElement>(null);
  const petSize = 100;
  const bottomPadding = 60;

  // ── 커스텀 대사 ──
  const { data: personalitiesData } = useDocument<PersonalitiesDoc>(
    'petSetting/personalities',
    { listen: true },
  );

  const mergedScripts = useMemo(() => {
    const base = { ...PetPersonalityTypeScripts } as Record<string, string[]>;
    const custom = personalitiesData?.scripts || {};
    Object.keys(custom).forEach((k) => {
      if (custom[k]?.length) base[k] = custom[k];
    });
    return base;
  }, [personalitiesData]);

  // ── 홈 위치 (하단 바닥) ──
  const getHome = useCallback(() => {
    if (containerBounds && containerBounds.height > 0) {
      return {
        x: (containerBounds.width - petSize) / 2,
        y: containerBounds.height - petSize - bottomPadding,
      };
    }
    return {
      x: (movement.boundaries.minX + movement.boundaries.maxX - petSize) / 2,
      y: movement.boundaries.maxY - petSize,
    };
  }, [containerBounds, movement.boundaries.minX, movement.boundaries.maxX, movement.boundaries.maxY]);

  // ── 렌더링용 React state ──
  const [petImgURL, setPetImgURL] = useState(pet.customImgURL || '');
  const [position, setPosition] = useState(getHome);
  const [interaction, setInteraction] = useState<InteractionType>('none');
  const [isHovered, setIsHovered] = useState(false);
  const [isDraggingState, setIsDraggingState] = useState(false);
  const [currentDialogue, setCurrentDialogue] = useState('');
  const dialogueTimerRef = useRef<NodeJS.Timeout>();
  const dialogueHideTimerRef = useRef<NodeJS.Timeout>();

  // ── 물리 전용 ref (React 렌더 사이클과 완전 분리) ──
  const posRef = useRef(getHome());
  const velRef = useRef(0);
  const rafRef = useRef(0);
  const lastTRef = useRef(0);
  // 'idle' | 'dragging' | 'falling' | 'walking'
  const phaseRef = useRef<string>('idle');
  const homeRef = useRef(getHome());
  const isDraggingRef = useRef(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const activePointerRef = useRef<number | null>(null);

  // ── ref 동기화 (외부 변경 반영) ──
  useEffect(() => {
    homeRef.current = getHome();
    if (phaseRef.current === 'idle') {
      posRef.current = homeRef.current;
      setPosition(homeRef.current);
    }
  }, [getHome]);

  useEffect(() => {
    setPetImgURL(pet.customImgURL || '');
  }, [pet.id, pet.customImgURL]);

  // ── posRef → React state 동기화 (렌더링 갱신) ──
  const syncPosition = useCallback(() => {
    setPosition({ ...posRef.current });
  }, []);

  // ──────────────────────────────────
  // 대사 시스템
  // ──────────────────────────────────
  const generateDialogue = useCallback(() => {
    if (!pet.personalities?.length) return '';
    const p = pet.personalities[
      Math.floor(Math.random() * pet.personalities.length)
    ] as string;
    const list = mergedScripts[p];
    if (!list?.length) return '';
    return list[Math.floor(Math.random() * list.length)];
  }, [pet.personalities, mergedScripts]);

  const startDialogueTimer = useCallback(() => {
    const next = () => {
      setCurrentDialogue(generateDialogue());
      dialogueHideTimerRef.current = setTimeout(
        () => setCurrentDialogue(''),
        5000 + Math.random() * 3000,
      );
      dialogueTimerRef.current = setTimeout(next, 60000 + Math.random() * 60000);
    };
    dialogueTimerRef.current = setTimeout(next, 5000);
  }, [generateDialogue]);

  useEffect(() => {
    startDialogueTimer();
    return () => {
      if (dialogueTimerRef.current) clearTimeout(dialogueTimerRef.current);
      if (dialogueHideTimerRef.current) clearTimeout(dialogueHideTimerRef.current);
    };
  }, [startDialogueTimer]);

  // ──────────────────────────────────
  // rAF 물리 루프 (React lifecycle과 독립)
  // useEffect 아님 — handlePointerUp에서 직접 시작
  // ──────────────────────────────────
  const runPhysicsLoop = useCallback(() => {
    const tick = (now: number) => {
      const dt = Math.min((now - lastTRef.current) / 1000, 0.05);
      lastTRef.current = now;

      const phase = phaseRef.current;
      const pos = posRef.current;
      const home = homeRef.current;

      if (phase === 'falling') {
        velRef.current += GRAVITY * dt;
        let newY = pos.y + velRef.current * dt;

        if (newY >= home.y) {
          newY = home.y;
          const bounce = -velRef.current * BOUNCE_DAMPING;

          if (Math.abs(bounce) < 30) {
            // 착지 → walking 전환 (루프 끊지 않음)
            velRef.current = 0;
            posRef.current = { x: pos.x, y: home.y };
            phaseRef.current = 'walking';
            setInteraction('walking');
          } else {
            velRef.current = bounce;
            posRef.current = { x: pos.x, y: home.y };
          }
        } else {
          posRef.current = { x: pos.x, y: newY };
        }

        syncPosition();
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      if (phase === 'walking') {
        const dx = home.x - pos.x;
        const dist = Math.abs(dx);

        if (dist < 1) {
          // 도착
          posRef.current = { x: home.x, y: home.y };
          phaseRef.current = 'idle';
          setInteraction('none');
          syncPosition();
          return; // 루프 자연 종료
        }

        const step = Math.min(WALK_SPEED * dt, dist);
        posRef.current = {
          x: pos.x + step * (dx > 0 ? 1 : -1),
          y: home.y,
        };

        syncPosition();
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      // idle/dragging → 루프 종료
    };

    lastTRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  }, [syncPosition]);

  // ──────────────────────────────────
  // 포인터 핸들러
  // ──────────────────────────────────
  const pointerToLocal = useCallback(
    (cx: number, cy: number) =>
      containerBounds
        ? { x: cx - containerBounds.left, y: cy - containerBounds.top }
        : { x: cx, y: cy },
    [containerBounds],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // 진행 중 루프 취소
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      const el = petRef.current;
      if (!el) return;
      activePointerRef.current = e.pointerId;
      el.setPointerCapture(e.pointerId);

      const local = pointerToLocal(e.clientX, e.clientY);
      dragOffsetRef.current = {
        x: local.x - posRef.current.x,
        y: local.y - posRef.current.y,
      };
      dragStartRef.current = local;
      isDraggingRef.current = false;
      phaseRef.current = 'idle'; // 이전 walking/falling 상태 리셋
      setInteraction('none');
    },
    [pointerToLocal],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (activePointerRef.current !== e.pointerId) return;

      const local = pointerToLocal(e.clientX, e.clientY);

      if (!isDraggingRef.current) {
        const dx = local.x - dragStartRef.current.x;
        const dy = local.y - dragStartRef.current.y;
        if (dx * dx + dy * dy < 25) return; // 5px 미만
        isDraggingRef.current = true;
        phaseRef.current = 'dragging';
        setIsDraggingState(true);
        setInteraction('drag');
      }

      const maxX = containerBounds
        ? containerBounds.width - petSize
        : movement.boundaries.maxX;
      const maxY = containerBounds
        ? containerBounds.height - petSize
        : movement.boundaries.maxY;

      posRef.current = {
        x: clamp(local.x - dragOffsetRef.current.x, 0, maxX),
        y: clamp(local.y - dragOffsetRef.current.y, 0, maxY),
      };
      syncPosition();
    },
    [pointerToLocal, containerBounds, movement.boundaries, syncPosition],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (activePointerRef.current !== e.pointerId) return;
      petRef.current?.releasePointerCapture(e.pointerId);
      activePointerRef.current = null;

      if (!isDraggingRef.current) return;
      isDraggingRef.current = false;
      setIsDraggingState(false);

      // falling 시작 — useEffect 없이 직접 루프 기동
      velRef.current = 0;
      phaseRef.current = 'falling';
      setInteraction('none');
      runPhysicsLoop();
    },
    [runPhysicsLoop],
  );

  // 클릭 (대사 + bounce)
  const handleClickPet = useCallback(() => {
    if (isDraggingRef.current) return;
    if (dialogueHideTimerRef.current) clearTimeout(dialogueHideTimerRef.current);
    setCurrentDialogue(generateDialogue());
    dialogueHideTimerRef.current = setTimeout(
      () => setCurrentDialogue(''),
      5000 + Math.random() * 3000,
    );
    setInteraction('click');
    setTimeout(() => {
      // walking 중이면 click이 끝난 뒤 walking으로 복구
      if (phaseRef.current === 'walking') {
        setInteraction('walking');
      } else {
        setInteraction('none');
      }
    }, 600);
  }, [generateDialogue]);

  // cleanup
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <PetContainer
      ref={petRef}
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={() => {
        handleClickPet();
        if (onClick && !isDraggingRef.current) onClick();
      }}
      $isHovered={isHovered}
      $isDragging={isDraggingState}
    >
      <PetImageWrapper $interaction={interaction}>
        <PetImage src={petImgURL} alt={pet.customName} draggable={false} />
      </PetImageWrapper>

      {currentDialogue && <DialogueBubble>{currentDialogue}</DialogueBubble>}
    </PetContainer>
  );
};

// ── Keyframes ──

const bounceSquish = keyframes`
  0%   { transform: scale(1, 1); }
  15%  { transform: scale(1.15, 0.85); }
  30%  { transform: scale(0.9, 1.1); }
  45%  { transform: scale(1.05, 0.95); }
  60%  { transform: scale(0.98, 1.02); }
  75%  { transform: scale(1.02, 0.98); }
  100% { transform: scale(1, 1); }
`;

const wiggle = keyframes`
  0%   { transform: rotate(0deg) scale(1.05, 0.95); }
  15%  { transform: rotate(-8deg) scale(1.08, 0.92); }
  30%  { transform: rotate(6deg) scale(1.06, 0.94); }
  45%  { transform: rotate(-5deg) scale(1.04, 0.96); }
  60%  { transform: rotate(3deg) scale(1.02, 0.98); }
  75%  { transform: rotate(-2deg) scale(1.01, 0.99); }
  100% { transform: rotate(0deg) scale(1, 1); }
`;

const waddle = keyframes`
  0%   { transform: rotate(0deg) translateY(0); }
  25%  { transform: rotate(-6deg) translateY(-3px); }
  50%  { transform: rotate(0deg) translateY(0); }
  75%  { transform: rotate(6deg) translateY(-3px); }
  100% { transform: rotate(0deg) translateY(0); }
`;

const getAnimation = ($i: InteractionType) => {
  switch ($i) {
    case 'click':
      return css`animation: ${bounceSquish} 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);`;
    case 'drag':
      return css`animation: ${wiggle} 0.5s ease-in-out infinite;`;
    case 'walking':
      return css`animation: ${waddle} 0.35s ease-in-out infinite;`;
    default:
      return '';
  }
};

// ── Styled ──

const PetContainer = styled.div<{ $isHovered: boolean; $isDragging: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  width: 100px;
  height: 100px;
  cursor: ${({ $isDragging }) =>
    $isDragging ? 'grabbing' : 'var(--hoverCursorURL), pointer'};
  transition: filter 0.2s ease;
  z-index: 1;
  touch-action: none;

  ${({ $isHovered }) =>
    $isHovered &&
    `filter: brightness(1.1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));`}
`;

const PetImageWrapper = styled.div<{ $interaction: InteractionType }>`
  width: 100%;
  height: 100%;
  transform-origin: center bottom;
  will-change: transform;
  ${({ $interaction }) => getAnimation($interaction)}
`;

const PetImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  pointer-events: none;
`;

const DialogueBubble = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, calc(-100% - 8px));
  background: white;
  color: #333;
  padding: 6px 10px;
  border-radius: 12px;
  font-size: 11px;
  width: max-content;
  word-break: keep-all;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border: 1px solid #ddd;
  z-index: 2;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-bottom: 0;
    border-top-color: white;
  }
`;

export default PetCharacter;
